// Presents localized success and expected-action notices dispatched by htmx responses.
import { showActionNotification, toastLabels, type NotificationKind } from './notifications'
import { Log } from './logger'
import { parseToastDetails, type ToastDetails } from './toast-details'

type ToastDetail = {
  details?: unknown
  items?: unknown
  message?: unknown
  type?: unknown
}

const TOAST_EVENT = 'dishBook:toast'
const initializedPageToasts = new WeakSet<HTMLElement>()

/**
 * Builds one dismissible toast without interpreting its localized message as markup.
 */
function showToast(
  message: string,
  type: NotificationKind,
  details?: ToastDetails,
): void {
  showActionNotification(
    message,
    type,
    details,
    toastLabels().details,
  )
}

/**
 * Validates each server notice and displays it, throwing invalidMessage for
 * any item without a message or with an unknown type.
 */
function showToastItems(items: unknown[], invalidMessage: string): void {
  items.forEach((item) => {
    if (typeof item !== 'object' || item === null) {
      throw new Error(invalidMessage)
    }

    const record = item as ToastDetail
    const message = typeof record.message === 'string' ? record.message.trim() : ''
    const type = record.type

    if (message === '' || (type !== 'success' && type !== 'error')) {
      throw new Error(invalidMessage)
    }

    let details: ToastDetails | undefined

    try {
      details = parseToastDetails(record.details)
    } catch (error) {
      throw new Error(invalidMessage, { cause: error })
    }

    showToast(message, type, details)
  })
}

/**
 * Validates and displays one server-originated htmx action notice.
 */
function handleToast(event: Event): void {
  Log.info('Handle server action notification', () => {
    const customEvent = event as CustomEvent<ToastDetail>
    const detail = customEvent.detail

    const items = Array.isArray(detail?.items) ? detail.items : [detail]

    showToastItems(items, 'An htmx toast event contained invalid data.')
  })
}

/** Reads inert server-rendered flash data and displays it through shared toasts. */
function initializePageToasts(): void {
  const element = document.getElementById('page-toasts')

  if (element === null || initializedPageToasts.has(element)) {
    return
  }

  initializedPageToasts.add(element)

  Log.notice('Display server action results', () => {
    const source = element.textContent ?? ''
    const items: unknown = JSON.parse(source)

    if (!Array.isArray(items)) {
      throw new Error('Server action notifications must be a list.')
    }

    showToastItems(items, 'A server action notification contained invalid data.')

    element.remove()
  })
}

document.body.addEventListener(TOAST_EVENT, handleToast)

initializePageToasts()
